import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

const withConnectionId = (path: string, connectionId: string) =>
    path.replace(':connectionId', connectionId);

const withSyncJobId = (path: string, syncJobId: string) =>
    path.replace(':syncJobId', syncJobId);

interface IntegrationToken {
    token: string;
}

export interface CreateIntegrationRequest {
    connection_id: string;
    scope: string;
    integration_type: string;
    display_name?: string;
    connection_data?: {
        host?: string;
        port?: number;
        username?: string;
        password?: string;
        private_key?: string | null;
    };
}

export interface CreateIntegrationConnectionRequest {
    channel_id: string;
    owner_type: 'user' | 'organization';
    display_name?: string;
    auth_type: string;
    credentials?: {
        api_key?: string;
    };
}

export interface ConfigureTelegramConnectionRequest {
    agent_id: string;
    bot_token: string;
}

export interface ConfigureWhatsAppConnectionRequest {
    agent_id: string;
    app_id: string;
    waba_id: string;
    phone_number_id: string;
    access_token: string;
    app_secret: string;
    webhook_verify_token: string;
}

export interface ConfigureTeamsConnectionRequest {
    agent_id: string;
    azure_tenant_id: string;
    microsoft_app_id: string;
    client_secret: string;
    client_secret_expires_at: string;
    bot_name: string;
    azure_bot_resource_name: string;
}

export interface ConfigureTeamsConnectionResponse {
    id?: string;
    connection_id?: string;
    webhook_url?: string;
    status?: string;
    [key: string]: unknown;
}

export interface ActivateTeamsConnectionResponse {
    connection_id?: string;
    agent_id?: string;
    status?: string;
    webhook_url?: string;
    bot_link?: string;
    [key: string]: unknown;
}

export interface ActivateWhatsAppConnectionResponse {
    connection_id: string;
    agent_id: string;
    status: string;
    webhook_url?: string;
}

export interface ActivateTelegramConnectionResponse {
    connection_id: string;
    agent_id: string;
    status: string;
    bot_username: string;
    bot_link: string;
    webhook_url: string;
}

export interface IntegrationProviders {
    connection_id?: string;
    provider_id: string;
    display_name: string;
    provider_family: string;
    integration_type?: string;
    auth_types: string[];
    supported_capabilities: string[];
    supported_scopes: string[];
    status: string;
    resource_browse_supported: boolean;
}

export interface AgentIntegrationOverviewProviderDefinition {
    provider_id: string;
    display_name: string;
    provider_family: string;
    auth_types: string[];
    supported_capabilities: string[];
    supported_scopes: string[];
    status: string;
    resource_browse_supported: boolean;
    indexed_resources_supported?: boolean;
    supported_actions?: string[];
}

export interface AgentIntegrationOverviewItem {
    provider: AgentIntegrationOverviewProviderDefinition;
    status: string;
    connection_id: string | null;
    display_name: string | null;
    connection_url: string | null;
    external: Record<string, unknown>;
    metadata: Record<string, unknown>;
    supported_actions?: string[];
    can_chat: boolean;
    last_tested_at: string | null;
}

export interface AgentIntegrationOverviewResponse {
    items: AgentIntegrationOverviewItem[];
    total: number;
}

export interface IntegrationLimitResponse {
    allowed: boolean;
    limit_type: string;
    current_usage: number;
    limit_value: number;
    remaining: number;
    message: string | null;
}

export interface InitiateConnectionRequest {
    provider_id: string;
    owner_type: 'user' | 'organization';
    redirect_uri: string;
}

export interface InitiateConnectionResponse {
    type: 'redirect' | 'widget';
    connection_id: string;
    redirect_url?: string;
    widget_provider?: string | null;
    widget_session?: string | null;
}

export interface CompleteConnectionRequest {
    provider_id: string;
    owner_type: 'user' | 'organization';
    params: {
        connection_id: string;
        source_connection_id: string;
    };
}

export interface AdapterBinding {
    CapabilityId: string;
    AdapterId: string;
    AdapterConfig: {
        source_short_name: string;
        collection_strategy?: string;
        selection_rule_types?: string[];
        selection_config_mode?: string;
    };
}

export interface CompleteConnectionResponse {
    id: string;
    provider_id: string;
    provider_family: string;
    owner_type: string;
    owner_id: string;
    display_name: string | null;
    status: string;
    granted_scopes: string[];
    enabled_capabilities: string[];
    adapter_bindings: AdapterBinding[];
    credential_ref: string;
    external_refs: {
        airweave?: {
            collection_id: string;
            source_connection_id: string;
        };
    };
    health: unknown;
    last_used_at: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface IntegrationConnectionsListParams {
    channel_id?: string;
    agent_id?: string;
    status?: string;
}

export interface TelegramConnectionExternalRefs {
    agent_id?: string;
    bot_id?: string;
    bot_username?: string;
    bot_link?: string;
    bot_name?: string;
}

export interface WhatsAppConnectionExternalRefs {
    waba_id?: string;
    phone_number_id?: string;
    display_phone_number?: string;
    verified_name?: string;
    agent_id?: string;
}

export interface TeamsConnectionExternalRefs {
    agent_id?: string;
    azure_tenant_id?: string;
    microsoft_app_id?: string;
    bot_name?: string;
    azure_bot_resource_name?: string;
    bot_link?: string;
}

export interface ActiveIntegrationConnection {
    id: string;
    provider_id: string;
    provider_family: string;
    owner_type: string;
    owner_id: string;
    display_name: string | null;
    status: string;
    granted_scopes: string[];
    enabled_capabilities: string[];
    adapter_bindings: AdapterBinding[];
    credential_ref: string;
    external?: Record<string, unknown>;
    connection_url?: string | null;
    agent_id?: string | null;
    bot_id?: string | null;
    bot_name?: string | null;
    bot_username?: string | null;
    external_refs: {
        airweave?: {
            collection_id: string;
            source_connection_id: string;
            source_short_name?: string;
        };
        telegram?: TelegramConnectionExternalRefs;
        whatsapp?: WhatsAppConnectionExternalRefs;
        teams?: TeamsConnectionExternalRefs;
    };
    health: unknown;
    last_used_at: string | null;
    schedule?: unknown;
    created_at: string | null;
    updated_at: string | null;
}

export interface IntegrationBrowseNode {
    source_node_id: string;
    node_type: string;
    title: string;
    has_children: boolean;
    item_count: number | null;
    description: string | null;
}

export interface IntegrationBrowseResponse {
    nodes: IntegrationBrowseNode[];
    parent_node_id: string | null;
    total: number;
}

export interface IntegrationSelectionNode {
    source_node_id: string;
    node_type: string;
    node_title: string;
}

export interface SaveIntegrationSelectionsRequest {
    nodes: IntegrationSelectionNode[];
}

export interface SaveIntegrationSelectionsResponse {
    nodes: IntegrationSelectionNode[];
}

export interface IntegrationIndexedResourceRef {
    node_id: string;
    node_type: string;
}

export interface IntegrationIndexedResource {
    id: string;
    connection_id: string;
    provider_id: string;
    external_id: string;
    resource_type: string;
    name: string;
    resource_ref: IntegrationIndexedResourceRef | null;
    uri: string | null;
    selection_state: string | null;
    updated_at_external: string | null;
    metadata: Record<string, unknown> | null;
}

export interface ListIntegrationIndexedResourcesParams {
    limit?: number;
    offset?: number;
    name_query?: string;
}

export interface IntegrationIndexedResourcesResponse {
    items: IntegrationIndexedResource[];
    total: number;
    limit: number;
    offset: number;
}

export interface StartSyncJobRequest {
    mode: 'incremental' | 'full';
    trigger: 'manual';
}

export interface IntegrationSyncJob {
    id: string;
    connection_id: string;
    provider_id: string;
    capability_id: string;
    adapter_id: string;
    trigger: string;
    mode: string;
    status: string;
    started_at: string | null;
    finished_at: string | null;
    stats: unknown;
    error: unknown;
    external_refs: {
        airweave?: {
            job_id: string;
        };
    };
    created_at: string | null;
}

export type IntegrationSyncJobListResponse = IntegrationSyncJob[];

let activeIntegrationConnectionsRequest: Promise<ActiveIntegrationConnection[]> | null = null;

export const getIntegrationToken = async (
    config: ConfigurationSettings
): Promise<IntegrationToken> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationToken}`;
    return clients.get(url, config.blocksKey);
};

export const createIntegration = async (
    config: ConfigurationSettings,
    payload: CreateIntegrationRequest
): Promise<IntegrationProviders> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrations}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const getIntegrations = async (
    config: ConfigurationSettings
): Promise<IntegrationProviders[]> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrations}`;
    return clients.get(url, config.blocksKey);
};

export const createIntegrationConnection = async (
    config: ConfigurationSettings,
    payload: CreateIntegrationConnectionRequest
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationConnectionCreate}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const configureTelegramConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: ConfigureTelegramConnectionRequest
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTelegramConnectionConfigure.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const activateTelegramConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<ActivateTelegramConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTelegramConnectionActivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const deactivateTelegramConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTelegramConnectionDeactivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export interface ConfigureWhatsAppConnectionResponse {
    id?: string;
    connection_id?: string;
    webhook_url?: string;
    status?: string;
}

export const configureWhatsAppConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: ConfigureWhatsAppConnectionRequest
): Promise<ConfigureWhatsAppConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationWhatsAppConnectionConfigure.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const bindWhatsAppConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    agentId: string
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationWhatsAppConnectionBind.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({ agent_id: agentId }));
};

export const activateWhatsAppConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<ActivateWhatsAppConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationWhatsAppConnectionActivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const deactivateWhatsAppConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationWhatsAppConnectionDeactivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const configureTeamsConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: ConfigureTeamsConnectionRequest
): Promise<ConfigureTeamsConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTeamsConnectionConfigure.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const activateTeamsConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<ActivateTeamsConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTeamsConnectionActivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const deactivateTeamsConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = config.paths.seliseAi.integrationTeamsConnectionDeactivate.replace(
        ':connectionId',
        connectionId
    );
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const testTeamsConnection = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<unknown> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = (
        config.paths.seliseAi.integrationTeamsConnectionTest ??
        '/channels/teams/connections/:connectionId/test'
    ).replace(':connectionId', connectionId);
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify({}));
};

export const testMessageTeamsConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: { text: string }
): Promise<unknown> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const path = (
        config.paths.seliseAi.integrationTeamsConnectionTestMessage ??
        '/channels/teams/connections/:connectionId/test-message'
    ).replace(':connectionId', connectionId);
    const url = `${baseUrl}${path}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

const EMPTY_PROVIDER: AgentIntegrationOverviewProviderDefinition = {
    provider_id: '',
    display_name: 'Integration',
    provider_family: '',
    auth_types: [],
    supported_capabilities: [],
    supported_scopes: [],
    status: '',
    resource_browse_supported: false,
};

type RawChannelOverviewItem = AgentIntegrationOverviewItem & {
    channel?: AgentIntegrationOverviewProviderDefinition & { channel_id?: string };
};

const normalizeOverviewItem = (item: RawChannelOverviewItem): AgentIntegrationOverviewItem => {
    const channel = item.channel;
    const providerSource = item.provider ?? channel;
    const provider: AgentIntegrationOverviewProviderDefinition = providerSource
        ? {
              ...EMPTY_PROVIDER,
              ...providerSource,
              provider_id:
                  providerSource.provider_id ||
                  channel?.channel_id ||
                  item.display_name ||
                  EMPTY_PROVIDER.provider_id,
          }
        : EMPTY_PROVIDER;

    return {
        ...item,
        provider,
        supported_actions: item.supported_actions ?? provider.supported_actions ?? [],
        external: item.external ?? {},
        metadata: item.metadata ?? {},
        can_chat: item.can_chat ?? false,
    };
};

export const getAgentIntegrationsOverview = async (
    config: ConfigurationSettings,
    agentId: string
): Promise<AgentIntegrationOverviewResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const query = new URLSearchParams({ agent_id: agentId });
    const url = `${baseUrl}${config.paths.seliseAi.integrationOverview}?${query.toString()}`;
    const response = await clients.get<
        AgentIntegrationOverviewResponse & { channels?: RawChannelOverviewItem[] }
    >(url, config.blocksKey);

    const items = (response.items ?? response.channels ?? []).map(normalizeOverviewItem);

    return {
        items,
        total: response.total ?? items.length,
    };
};

export const getIntegrationConnections = async (
    config: ConfigurationSettings,
    params?: IntegrationConnectionsListParams
): Promise<ActiveIntegrationConnection[]> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const searchParams = new URLSearchParams();

    if (params?.channel_id) {
        searchParams.set('channel_id', params.channel_id);
    }

    if (params?.agent_id) {
        searchParams.set('agent_id', params.agent_id);
    }

    if (params?.status) {
        searchParams.set('status', params.status);
    }

    const query = searchParams.toString();
    const url = `${baseUrl}${config.paths.seliseAi.integrationConnectionCreate}${
        query ? `?${query}` : ''
    }`;
    const response = await clients.get<
        ActiveIntegrationConnection[] | { items: ActiveIntegrationConnection[] }
    >(url, config.blocksKey);

    if (Array.isArray(response)) {
        return response;
    }

    return response.items ?? [];
};

export const getActiveIntegrationConnections = async (
    config: ConfigurationSettings
): Promise<ActiveIntegrationConnection[]> => {
    if (activeIntegrationConnectionsRequest) {
        return activeIntegrationConnectionsRequest;
    }

    const request = (async () => {
        const baseUrl = `${config.paths.seliseAiBaseUrl}`;
        const url = `${baseUrl}${config.paths.seliseAi.integrationConnections}`;
        const response = await clients.get<
            ActiveIntegrationConnection[] | { items: ActiveIntegrationConnection[] }
        >(url, config.blocksKey);

        if (Array.isArray(response)) {
            return response;
        }

        return response.items ?? [];
    })().finally(() => {
        activeIntegrationConnectionsRequest = null;
    });

    activeIntegrationConnectionsRequest = request;

    return request;
};

export const deleteIntegration = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<void> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.deleteIntegrationConnection}/${connectionId}`;
    return clients.delete(url, config.blocksKey);
};

export const browseIntegrationConnection = async (
    config: ConfigurationSettings,
    connectionId: string,
    parentNodeId?: string
): Promise<IntegrationBrowseResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const query = parentNodeId ? `?parent_node_id=${encodeURIComponent(parentNodeId)}` : '';
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionBrowse, connectionId)}${query}`;
    return clients.get(url, config.blocksKey);
};

export const getIntegrationSelections = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<IntegrationSelectionNode[]> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionSelections, connectionId)}`;
    return clients.get(url, config.blocksKey);
};

export const saveIntegrationSelections = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: SaveIntegrationSelectionsRequest
): Promise<SaveIntegrationSelectionsResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionSelections, connectionId)}`;
    return clients.put(url, config.blocksKey, JSON.stringify(payload));
};

export const clearIntegrationSelections = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<void> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionSelections, connectionId)}`;
    return clients.delete(url, config.blocksKey);
};

export const listIntegrationIndexedResources = async (
    config: ConfigurationSettings,
    connectionId: string,
    params: ListIntegrationIndexedResourcesParams = {}
): Promise<IntegrationIndexedResourcesResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const searchParams = new URLSearchParams();
    const limit = Math.min(params.limit ?? 10, 200);
    const offset = Math.max(params.offset ?? 0, 0);

    searchParams.set('limit', String(limit));
    searchParams.set('offset', String(offset));

    if (params.name_query?.trim()) {
        searchParams.set('name_query', params.name_query.trim());
    }

    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionResources, connectionId)}?${searchParams.toString()}`;
    return clients.get(url, config.blocksKey);
};

export const startIntegrationSyncJob = async (
    config: ConfigurationSettings,
    connectionId: string,
    payload: StartSyncJobRequest
): Promise<IntegrationSyncJob> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionSyncJobs, connectionId)}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const listIntegrationSyncJobs = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<IntegrationSyncJobListResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withConnectionId(config.paths.seliseAi.integrationConnectionSyncJobs, connectionId)}`;
    return clients.get(url, config.blocksKey);
};

export const getIntegrationSyncJob = async (
    config: ConfigurationSettings,
    syncJobId: string
): Promise<IntegrationSyncJob> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${withSyncJobId(config.paths.seliseAi.integrationSyncJobById, syncJobId)}`;
    return clients.get(url, config.blocksKey);
};

export const syncIntegration = async (
    config: ConfigurationSettings,
    connectionId: string
): Promise<{ success: boolean; message: string }> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationSync}`;
    return clients.post(url, config.blocksKey, JSON.stringify({ connection_id: connectionId }));
};

export const checkIntegrationLimit = async (
    config: ConfigurationSettings
): Promise<IntegrationLimitResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationLimit}`;
    return clients.get(url, config.blocksKey);
};

export const initiateConnection = async (
    config: ConfigurationSettings,
    payload: InitiateConnectionRequest
): Promise<InitiateConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationConnectionInitiate}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const completeConnection = async (
    config: ConfigurationSettings,
    payload: CompleteConnectionRequest
): Promise<CompleteConnectionResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.integrationConnectionCallback}`;
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};
